import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import BtnTooltip from '../../../../../../UI/BtnTooltip'
import NewsCover from '../../../../../../UI/NewsCover'

import { newsLocation } from '../../../../../../shared/helpers'

import { formatVienna } from '../../../../../../shared/datetime'
import styles from './style.module.scss'

/*
 * Eine Nachricht als Karte.
 *
 * Die alte Tabelle hatte elf Spalten, von denen die meisten "Alle" anzeigten —
 * jede Zeile war damit gleich breit wie die breiteste, und die Seite liess sich
 * nur noch waagrecht durchscrollen. Die Karte dreht das um: Sie zeigt nur die
 * Einschraenkungen, die tatsaechlich gesetzt sind. Ist keine gesetzt, steht
 * dort ein einziger Satz statt fuenf Mal "Alle".
 *
 * Ohne "facets" ist es dieselbe Karte fuer Schueler:innen und Lehrkraefte:
 * Zielgruppen, Status und Autor sind Angaben der Redaktion — wer die Nachricht
 * liest, kann damit nichts anfangen. Uebrig bleibt, was gelesen wird.
 */

const EXCERPT_LENGTH = 180
const VISIBLE_CHIPS = 2

const fmt = value => (value ? formatVienna(value, 'dd MMM RRRR') : null)
/*
 * Zwei Zeitpunkte als Spanne — aber nur, wenn es wirklich zwei sind. Eine
 * Veranstaltung an einem Tag stand sonst als „12 Nov 2026 – 12 Nov 2026" da.
 */
const spanne = (von, bis) => (von && bis && von !== bis ? `${von} – ${bis}` : von || bis || '')

function Facet({ label, values }) {
	const shown = values.slice(0, VISIBLE_CHIPS)
	const rest = values.length - shown.length

	return (
		<div className={styles.facet} data-tip={values.length > VISIBLE_CHIPS ? values.join(', ') : undefined}>
			<span className={styles.facetLabel}>{label}</span>
			{shown.map(value => (
				<span key={value} className={styles.facetChip}>
					{value}
				</span>
			))}
			{rest > 0 && <span className={styles.facetMore}>+{rest}</span>}
		</div>
	)
}

Facet.propTypes = {
	label: PropTypes.string.isRequired,
	values: PropTypes.arrayOf(PropTypes.string).isRequired
}

export default function NewsCard(props) {
	const { t } = useTranslation()
	const { item, facets } = props

	// Die Adminansicht liefert die Zielgruppen mit; die Lesenden bekommen sie
	// nicht — und damit auch nicht Status und Autor.
	const zeigtVerteilung = Boolean(facets)

	/*
	 * Zwei verschiedene Angaben, seit 08.08.2026 auch zwei Felderpaare:
	 *
	 *   eventStart/eventEnd  wann die Veranstaltung stattfindet
	 *   startDate/endDate    wie lange die Redaktion sie anzeigt
	 *
	 * Die Lesenden bekommen den Termin — die Anzeigedauer ist eine Angabe der
	 * Redaktion und wirkt unter einer Meldung wie ein Termin, den es nicht gibt.
	 * Die Redaktion sieht beides: Sie pflegt schliesslich beides.
	 */
	const termin = spanne(fmt(item.eventStart), fmt(item.eventEnd))
	const anzeige = spanne(fmt(item.startDate), fmt(item.endDate))
	const ort = newsLocation(item)

	const excerpt =
		item.description?.length > EXCERPT_LENGTH
			? `${item.description.substring(0, EXCERPT_LENGTH).trimEnd()}…`
			: item.description

	const istEntwurf = item.status === 'Draft'
	// Angeheftet heisst: Die Nachricht steht bei Schueler:innen und Lehrkraeften
	// vor allen anderen. Der Server legt dafuer den Zeitpunkt ab.
	const istAngeheftet = Boolean(item.pinnedAt)

	const actions = [
		props.onEdit && { label: t('Edit'), onClick: () => props.onEdit(item.id) },
		// Zwischen Bearbeiten und Entfernen: Duplizieren ist der zweite Weg zu
		// einer Nachricht, die es so aehnlich schon gibt — dieselbe
		// Veranstaltung fuer ein weiteres Bundesland, derselbe Aufruf im
		// naechsten Jahr.
		props.onDuplicate && { label: t('Duplicate'), onClick: () => props.onDuplicate(item.id) },
		/*
		 * Der eine Mausklick aus Anforderung #164: aktiv ↔ Entwurf, ohne das
		 * Formular zu oeffnen und ohne am Datum zu drehen. Ein Entwurf bleibt
		 * fuer Schueler:innen und Lehrkraefte verborgen.
		 */
		props.onToggleDraft && {
			label: istEntwurf ? t('Activate message') : t('Set to draft'),
			onClick: () => props.onToggleDraft(item.id, !istEntwurf)
		},
		/*
		 * Anheften — der zweite Teil von Anforderung #164: Eine Nachricht, die
		 * zuerst gesehen werden soll, steht vorne, ohne dass jemand an den
		 * Datumsangaben dreht. Nur die Bundesadministration bekommt den
		 * Eintrag; die Ansicht reicht die Handlung nur fuer sie herein.
		 */
		props.onTogglePin && {
			label: istAngeheftet ? t('Unpin message') : t('Pin message'),
			onClick: () => props.onTogglePin(item.id, !istAngeheftet)
		},
		props.onDelete && {
			label: t('Remove'),
			style: { color: '#eb5757' },
			onClick: () => props.onDelete(item.id)
		}
	].filter(Boolean)

	return (
		// Ein Entwurf hebt sich als ganze Karte ab (grau hinterlegt), nicht nur
		// am Abzeichen — die Anforderung verlangt, dass man ihn sofort erkennt.
		<article className={clsx(styles.card, istEntwurf && styles.cardDraft)}>
			<div className={styles.head}>
				<div className={styles.badges}>
					{item.newsType && (
						<span
							className={clsx(
								styles.badgeType,
								item.newsType === 'EVENT' ? styles.badgeEvent : styles.badgeNews
							)}
						>
							{t(item.newsType)}
						</span>
					)}
					{/* Angeheftet steht vor dem Status: Es ist die Ausnahme,
					    und die Verwaltung soll sie zuerst sehen. */}
					{zeigtVerteilung && istAngeheftet && (
						<span className={styles.badgePinned}>{t('Pinned to top')}</span>
					)}
					{zeigtVerteilung && item.status && (
						<span className={clsx(styles.badgeStatus, styles[`status${item.status}`])}>
							{t(item.status)}
						</span>
					)}
				</div>
				{!!actions.length && <BtnTooltip className={styles.actions} items={actions} />}
			</div>

			<button type="button" className={styles.body} onClick={() => props.onOpen(item)}>
				{/* Das Titelbild steht ganz oben — es ist das, was die Karte auf
				    einen Blick als Ankuendigung erkennbar macht. Ohne Bild bleibt
				    die Stelle leer, statt eine graue Flaeche zu zeigen. */}
				<NewsCover className={styles.cover} hashName={item.imageHashName} alt={item.title} />

				<h3 className={styles.title}>{item.title}</h3>

				{(termin || ort) && (
					<p className={styles.meta}>
						{!!termin && (
							<span>
								{termin}
								{!!ort && <span className={styles.metaSeparator}>·</span>}
							</span>
						)}
						{!!ort && <span>{ort}</span>}
					</p>
				)}

				{/*
				 * Nur fuer die Redaktion: wie lange die Nachricht laeuft. Sie steht
				 * abgesetzt und benannt — sonst liest sie sich als zweiter Termin.
				 */}
				{zeigtVerteilung && !!anzeige && (
					<p className={styles.display}>
						{t('Shown')}: {anzeige}
					</p>
				)}

				<p className={styles.excerpt}>{excerpt}</p>
				<span className={styles.readMore}>
					{t('read more')}
					{!!item.externalLink && <span className={styles.hasLink}>{t('External Link')}</span>}
				</span>
			</button>

			{zeigtVerteilung && (
				<div className={styles.foot}>
					{facets.length ? (
						<div className={styles.facets}>
							{facets.map(facet => (
								<Facet key={facet.key} label={facet.label} values={facet.values} />
							))}
						</div>
					) : (
						<p className={styles.audienceAll}>{t('Visible to all users')}</p>
					)}
					{!!item.creator?.email && <p className={styles.author}>{item.creator.email}</p>}
				</div>
			)}
		</article>
	)
}

NewsCard.propTypes = {
	item: PropTypes.object.isRequired,
	onOpen: PropTypes.func.isRequired,

	// Nur die tatsaechlich gesetzten Einschraenkungen; leer = an alle.
	// Fehlt die Angabe ganz, bleibt der Fuss der Karte weg (Lesende).
	facets: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			values: PropTypes.arrayOf(PropTypes.string).isRequired
		})
	),

	onEdit: PropTypes.func,
	onDuplicate: PropTypes.func,
	// (id, isDraft) — aktiv ↔ Entwurf, der Schnellweg aus dem Menue.
	onToggleDraft: PropTypes.func,
	// (id, isPinned) — oben anheften und wieder loesen. Nur Bundesadmins.
	onTogglePin: PropTypes.func,
	onDelete: PropTypes.func
}
